<script setup lang="ts">
/**
 * Servicekatalog — the Contracts team's quote editor (ADR 0060 phase 5).
 *
 * A port of Marcus Olssen's prototype into DOIT AI: the same two tabs
 * (Tilbud, Katalog), the same rows, totals and "Skal afklares", but the
 * data lives in the platform. The catalogue comes from the shared tables,
 * every amount is priced by the server (the same code the chat tool
 * uses), and a saved quote is the record the chat can find, approve and
 * turn into a contract. A price typed on a line is a deviation for THIS
 * quote; the catalogue's defaults are config, not editable here.
 *
 * The helper in the corner is the assistant's chat with the current draft
 * attached as context; when a turn changes the saved quote, the page
 * reloads it.
 */
import { computed, onMounted, reactive, ref, watch } from "vue";

import {
  serviceCatalogApi as api,
  type CatalogProduct,
  type CatalogQuestion,
  type CatalogView,
  type Customer,
  type EditableProduct,
  type LineInput,
  type Me,
  type Party,
  type PricePayload,
  type Quote,
  type QuoteSummary,
  ServiceCatalogError,
} from "../api/serviceCatalog";
import AssistantPage, { type PageTab } from "../components/AssistantPage.vue";
import HelperPanel from "../components/HelperPanel.vue";
import { useConfirm } from "../composables/useConfirm";
import { useHelperChat } from "../composables/useHelperChat";
import { useToast } from "../composables/useToast";

const props = defineProps<{ devUser: string }>();
const emit = defineEmits<{ (e: "exit"): void }>();

const { confirm } = useConfirm();
const toast = useToast();

// ── data ────────────────────────────────────────────────────────────────
const loading = ref(true);
const loadError = ref("");
const me = ref<Me | null>(null);
const catalog = ref<CatalogView | null>(null);
const priced = ref<PricePayload | null>(null);
const pricing = ref(false);
const saving = ref(false);

interface LineState {
  quantity: number;
  unitPrice: number | null; // a deviation for this quote; null = list price
}
const lines = reactive<Record<string, LineState>>({});
const customer = reactive({
  name: "",
  cvr: "",
  address: "",
  contact_name: "",
  contact_email: "",
  contact_phone: "",
});
const answers = reactive<Record<string, string>>({});
const title = ref("");
const notes = ref("");
const quote = ref<Quote | null>(null);
const dirty = ref(false);

// ── ui state ────────────────────────────────────────────────────────────
const tab = ref<"tilbud" | "katalog">("tilbud");
const search = ref("");
const filter = ref<"alle" | "valgte" | "udenpris" | "regler">("alle");
const closed = reactive(new Set<string>());
const quotesOpen = ref(false);
const quoteSearch = ref("");
const quoteList = ref<QuoteSummary[]>([]);
const quotesLoading = ref(false);

const PERIOD_SHORT: Record<string, string> = { monthly: "md.", annual: "år", onetime: "engangs" };
const PERIOD_LONG: Record<string, string> = { monthly: "Månedlig", annual: "Årlig", onetime: "Engangs" };
const RULE_LABEL: Record<string, string> = {
  unit: "Pr. enhed",
  tier: "Mængdetrin",
  group: "Gruppetrin",
  formula: "Formel",
};
const STATUS_LABEL: Record<string, string> = {
  draft: "kladde",
  sent: "sendt",
  approved: "godkendt",
  contract: "kontrakt",
  cancelled: "annulleret",
};
const PARTY_LABEL: Record<Party, string> = { customer: "Kunde", seller: "Sælger", ops: "Drift" };

// ── formatting ──────────────────────────────────────────────────────────
const money = (v: number | null | undefined, cur = "DKK"): string => {
  if (v === null || v === undefined) return "—";
  const dec = Math.abs(v % 1) > 0.004 ? 2 : 0;
  return new Intl.NumberFormat("da-DK", {
    style: "currency",
    currency: cur,
    minimumFractionDigits: dec,
    maximumFractionDigits: dec,
  }).format(v);
};
const num = (v: number | null | undefined): string =>
  v === null || v === undefined
    ? ""
    : new Intl.NumberFormat("da-DK", { maximumFractionDigits: 4 }).format(v);
const today = (): string => new Date().toISOString().slice(0, 10);

// ── search (folded substring / subsequence, the prototype's idea, lighter) ──
const fold = (s: string): string =>
  s
    .toLowerCase()
    .replace(/æ/g, "ae")
    .replace(/ø/g, "oe")
    .replace(/å/g, "aa");
function subsequence(term: string, hay: string): boolean {
  let i = 0;
  for (let j = 0; j < hay.length && i < term.length; j++) if (hay[j] === term[i]) i++;
  return i === term.length;
}
function matches(p: CatalogProduct, q: string): boolean {
  if (!q) return true;
  const hay = fold([p.name, p.category, p.unit, p.note, p.product_id].join(" "));
  return fold(q)
    .split(/\s+/)
    .filter(Boolean)
    .every((t) => hay.includes(t) || subsequence(t, fold(p.name)));
}

// ── derived ─────────────────────────────────────────────────────────────
const products = computed<CatalogProduct[]>(() => catalog.value?.products ?? []);
const categories = computed<string[]>(() => {
  const seen: string[] = [];
  for (const p of products.value) if (!seen.includes(p.category)) seen.push(p.category);
  return seen;
});
const qtyOf = (id: string): number => lines[id]?.quantity ?? 0;
const chosenIds = computed(() => Object.keys(lines).filter((id) => qtyOf(id) > 0));
const listUnitPrice = (p: CatalogProduct): number | null => {
  if (p.rule_type === "unit" || p.rule_type === "group") return p.unit_price;
  if (p.rule_type === "tier") {
    const q = qtyOf(p.product_id) || 1;
    const t = p.tiers.find((t) => q >= t.from_qty && (t.to_qty === null || q <= t.to_qty));
    return t ? t.unit_price : null;
  }
  const ln = pricedLine(p.product_id);
  return ln?.list_unit_price ?? null;
};
const pricedLine = (id: string) => priced.value?.lines.find((l) => l.product_id === id);
const passesFilter = (p: CatalogProduct): boolean => {
  if (filter.value === "valgte") return qtyOf(p.product_id) > 0;
  if (filter.value === "udenpris") return p.rule_type === "unit" && p.unit_price === null;
  if (filter.value === "regler") return p.rule_type !== "unit";
  return true;
};
const shown = computed(() => products.value.filter((p) => passesFilter(p) && matches(p, search.value)));
const counts = computed(() => ({
  alle: products.value.length,
  valgte: chosenIds.value.length,
  udenpris: products.value.filter((p) => p.rule_type === "unit" && p.unit_price === null).length,
  regler: products.value.filter((p) => p.rule_type !== "unit").length,
}));
const questionsFor = (cat: string): CatalogQuestion[] =>
  (catalog.value?.questions ?? []).filter((q) => q.category === cat);
const openQuestions = computed(() =>
  (priced.value?.service_questions ?? []).filter((q) => !(answers[q.label] ?? "").trim()),
);
const unpricedChosen = computed(() =>
  products.value.filter(
    (p) => qtyOf(p.product_id) > 0 && pricedLine(p.product_id)?.status === "no-price",
  ),
);
const editable = computed(() => !quote.value || ["draft", "sent"].includes(quote.value.status));
const can = (action: string): boolean => !!me.value?.can?.[action];

// ── loading ─────────────────────────────────────────────────────────────
onMounted(async () => {
  try {
    const [m, c] = await Promise.all([api.me(props.devUser), api.catalog(props.devUser)]);
    me.value = m;
    catalog.value = c;
    const hash = window.location.hash.match(/service-catalog\/(T-[\w-]+)/);
    if (hash) await loadQuote(hash[1]);
  } catch (err) {
    loadError.value = err instanceof Error ? err.message : String(err);
  } finally {
    loading.value = false;
  }
});

function lineInputs(): LineInput[] {
  const out: LineInput[] = [];
  for (const p of products.value) {
    const st = lines[p.product_id];
    if (!st || st.quantity <= 0) continue;
    const ln: LineInput = { product_id: p.product_id, quantity: st.quantity };
    if (st.unitPrice !== null && st.unitPrice !== undefined) ln.unit_price = st.unitPrice;
    out.push(ln);
  }
  return out;
}

let priceTimer: number | null = null;
async function reprice(): Promise<void> {
  const inputs = lineInputs();
  if (!inputs.length) {
    priced.value = null;
    return;
  }
  pricing.value = true;
  try {
    priced.value = await api.price(inputs, undefined, props.devUser);
  } catch (err) {
    toast.error("Prisberegning fejlede", err instanceof Error ? err.message : String(err));
  } finally {
    pricing.value = false;
  }
}
function schedulePricing(): void {
  dirty.value = true;
  if (priceTimer) window.clearTimeout(priceTimer);
  priceTimer = window.setTimeout(() => void reprice(), 250);
}

function setQty(id: string, raw: string): void {
  const q = Number(raw);
  if (!lines[id]) lines[id] = { quantity: 0, unitPrice: null };
  lines[id].quantity = Number.isFinite(q) && q > 0 ? q : 0;
  schedulePricing();
}
function setPrice(id: string, raw: string): void {
  if (!lines[id]) lines[id] = { quantity: 0, unitPrice: null };
  lines[id].unitPrice = raw === "" ? null : Number(raw);
  schedulePricing();
}
function clearAll(): void {
  for (const id of Object.keys(lines)) delete lines[id];
  priced.value = null;
  dirty.value = true;
}

function applyQuote(q: Quote): void {
  quote.value = q;
  for (const id of Object.keys(lines)) delete lines[id];
  for (const ln of q.lines) {
    lines[ln.product_id] = {
      quantity: ln.quantity,
      unitPrice: ln.override ? ln.unit_price : null,
    };
  }
  customer.name = q.customer_name;
  customer.cvr = q.customer_cvr;
  customer.address = q.customer_address;
  customer.contact_name = q.contact_name;
  customer.contact_email = q.contact_email;
  customer.contact_phone = q.contact_phone;
  for (const k of Object.keys(answers)) delete answers[k];
  Object.assign(answers, q.answers ?? {});
  title.value = q.title;
  notes.value = q.notes;
  dirty.value = false;
  history.replaceState(null, "", `#/service-catalog/${q.quote_id}`);
  void reprice();
}

async function loadQuote(id: string): Promise<void> {
  try {
    const { quote: q } = await api.getQuote(id, props.devUser);
    applyQuote(q);
    quotesOpen.value = false;
  } catch (err) {
    toast.error(`Kunne ikke hente ${id}`, err instanceof Error ? err.message : String(err));
  }
}

function newQuote(): void {
  quote.value = null;
  clearAll();
  Object.assign(customer, {
    name: "",
    cvr: "",
    address: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
  });
  for (const k of Object.keys(answers)) delete answers[k];
  title.value = "";
  notes.value = "";
  dirty.value = false;
  history.replaceState(null, "", "#/service-catalog");
}

async function deleteQuote(q: { quote_id: string; customer_name: string; status: string; contract_id?: string | null }): Promise<void> {
  const contract = q.status === "contract" && q.contract_id;
  const ok = await confirm({
    title: `Slet ${q.quote_id}?`,
    message:
      `${q.customer_name || "Tilbuddet"} forsvinder fra listen og kan ikke hentes igen.` +
      (contract ? ` Kontrakten ${q.contract_id} slettes også.` : "") +
      ` Nummeret ${q.quote_id} bliver ikke brugt igen.`,
    confirmLabel: "Slet",
    danger: true,
  });
  if (!ok) return;
  try {
    await api.deleteQuote(q.quote_id, props.devUser);
    quoteList.value = quoteList.value.filter((x) => x.quote_id !== q.quote_id);
    if (quote.value?.quote_id === q.quote_id) {
      newQuote();
      history.replaceState(null, "", "#/service-catalog");
    }
    toast.success(`${q.quote_id} er slettet`);
  } catch (err) {
    fail(err, "Tilbuddet blev ikke slettet");
  }
}

async function openQuotes(): Promise<void> {
  quotesOpen.value = true;
  quotesLoading.value = true;
  try {
    quoteList.value = (await api.listQuotes(quoteSearch.value, "", props.devUser)).quotes;
  } catch (err) {
    toast.error("Kunne ikke hente tilbud", err instanceof Error ? err.message : String(err));
  } finally {
    quotesLoading.value = false;
  }
}
watch(quoteSearch, () => {
  if (quotesOpen.value) void openQuotes();
});

// ── actions (all through the backend's action tools) ────────────────────
function fail(err: unknown, what: string): void {
  const msg = err instanceof ServiceCatalogError ? err.message : String(err);
  toast.error(what, msg);
}

async function save(): Promise<void> {
  if (!customer.name.trim()) {
    toast.warning("Kundens navn mangler", "Et tilbud skal sige, hvem det er til.");
    return;
  }
  const inputs = lineInputs();
  if (!inputs.length) {
    toast.warning("Ingen linjer", "Skriv et antal på mindst ét produkt.");
    return;
  }
  saving.value = true;
  try {
    const { quote: q } = await api.saveQuote(
      {
        quote_id: quote.value?.quote_id,
        customer: { ...customer },
        lines: inputs,
        title: title.value,
        notes: notes.value,
        answers: { ...answers },
      },
      props.devUser,
    );
    // The save result comes from the action tool, which does not know the
    // caller's rights; whoever just saved a draft may delete it (the server
    // still decides).
    applyQuote({ ...q, can_delete: quote.value?.can_delete ?? ["draft", "sent"].includes(q.status) });
    toast.success(`Gemt som ${q.quote_id}`, q.status === "draft" ? "Status: kladde" : "");
  } catch (err) {
    fail(err, "Tilbuddet blev ikke gemt");
  } finally {
    saving.value = false;
  }
}

const exporting = ref(false);

/** The offer as a Word file. Built from the SAVED record, so an unsaved or
 * changed draft is saved first — the document must match what the record
 * says, not what the screen showed a moment ago. */
async function exportOffer(what: "offer" | "agreement" = "offer"): Promise<void> {
  if (!quote.value || dirty.value) {
    if (!can("save_quote")) {
      toast.warning("Gem tilbuddet først", "Dokumentet bygges fra det gemte tilbud.");
      return;
    }
    await save();
    if (!quote.value || dirty.value) return;
  }
  exporting.value = true;
  try {
    const filename =
      what === "agreement"
        ? await api.downloadQuoteAgreement(quote.value.quote_id, props.devUser)
        : await api.downloadQuoteDocument(quote.value.quote_id, props.devUser);
    toast.success(what === "agreement" ? "Serviceaftalen (udkast) er hentet" : "Tilbuddet er hentet", filename);
  } catch (err) {
    fail(err, "Dokumentet kunne ikke hentes");
  } finally {
    exporting.value = false;
  }
}

async function exportContract(): Promise<void> {
  const id = quote.value?.contract_id;
  if (!id) return;
  exporting.value = true;
  try {
    const filename = await api.downloadContractAgreement(id, props.devUser);
    toast.success("Serviceaftalen er hentet", filename);
  } catch (err) {
    fail(err, "Dokumentet kunne ikke hentes");
  } finally {
    exporting.value = false;
  }
}

async function setStatus(next: "draft" | "sent" | "cancelled"): Promise<void> {
  if (!quote.value) return;
  const id = quote.value.quote_id;
  if (next === "cancelled") {
    const ok = await confirm({
      title: `Annullér ${id}?`,
      message: "Tilbuddet bliver stående som annulleret og kan ikke ændres bagefter.",
      confirmLabel: "Annullér tilbuddet",
      danger: true,
    });
    if (!ok) return;
  }
  try {
    const { quote: q } = await api.setStatus(id, next, "", props.devUser);
    applyQuote(q);
    toast.success(`${id} er nu ${STATUS_LABEL[q.status] ?? q.status}`);
  } catch (err) {
    fail(err, "Status blev ikke ændret");
  }
}

const approvalName = ref("");
async function approve(party: Party): Promise<void> {
  if (!quote.value) return;
  const id = quote.value.quote_id;
  const who = approvalName.value.trim();
  const ok = await confirm({
    title: `Registrér ${PARTY_LABEL[party].toLowerCase()}ns godkendelse?`,
    message: `${PARTY_LABEL[party]} har godkendt ${id}${who ? ` ved ${who}` : ""}. Det registreres med dit navn og tidspunkt.`,
    confirmLabel: "Registrér",
  });
  if (!ok) return;
  try {
    const { quote: q } = await api.approve(id, party, who, "", props.devUser);
    applyQuote(q);
    approvalName.value = "";
    toast.success(
      q.status === "approved" ? `${id} er godkendt af alle tre parter` : "Godkendelse registreret",
    );
  } catch (err) {
    fail(err, "Godkendelsen blev ikke registreret");
  }
}

const convertOpen = ref(false);
const convertForm = reactive({ start_date: today(), term_months: 12, notice_months: 3, notes: "" });
async function convert(): Promise<void> {
  if (!quote.value) return;
  const id = quote.value.quote_id;
  const ok = await confirm({
    title: `Opret kontrakt for ${id}?`,
    message: `Start ${convertForm.start_date}, ${convertForm.term_months} mdr. binding, ${convertForm.notice_months} mdr. opsigelse. Tilbuddet låses.`,
    confirmLabel: "Opret kontrakt",
  });
  if (!ok) return;
  try {
    const { contract } = await api.convert(
      id,
      {
        start_date: convertForm.start_date,
        term_months: convertForm.term_months || undefined,
        notice_months: convertForm.notice_months || undefined,
        notes: convertForm.notes,
      },
      props.devUser,
    );
    convertOpen.value = false;
    await loadQuote(id);
    toast.success(`Kontrakt ${contract.contract_id} oprettet`, "Bed hjælperen om kontraktdokumentet.");
  } catch (err) {
    fail(err, "Kontrakten blev ikke oprettet");
  }
}

// ── customer lookup ─────────────────────────────────────────────────────
const customerHits = ref<Customer[]>([]);
let lookupTimer: number | null = null;
function onCustomerInput(): void {
  dirty.value = true;
  if (lookupTimer) window.clearTimeout(lookupTimer);
  const q = customer.name.trim();
  if (q.length < 2) {
    customerHits.value = [];
    return;
  }
  lookupTimer = window.setTimeout(async () => {
    try {
      customerHits.value = (await api.customers(q, props.devUser)).customers;
    } catch {
      customerHits.value = [];
    }
  }, 300);
}
function hideHits(): void {
  window.setTimeout(() => (customerHits.value = []), 150);
}
function pickCustomer(c: Customer): void {
  customer.name = c.name;
  if (c.cvr) customer.cvr = c.cvr;
  if (c.address && !customer.address) customer.address = c.address;
  customerHits.value = [];
  dirty.value = true;
}

// ── helper chat ─────────────────────────────────────────────────────────
function pageContext(): string {
  const parts: string[] = [];
  if (quote.value) {
    parts.push(
      `Saved quote ${quote.value.quote_id} (status ${quote.value.status}${dirty.value ? ", with unsaved edits on screen" : ""}).`,
    );
  } else {
    parts.push("Unsaved draft (not stored yet).");
  }
  if (customer.name) {
    parts.push(
      `Customer: ${customer.name}${customer.cvr ? `, CVR ${customer.cvr}` : ""}${customer.contact_name ? `, contact ${customer.contact_name}` : ""}.`,
    );
  }
  const ls = lineInputs().map((l) => {
    const p = products.value.find((x) => x.product_id === l.product_id);
    return `${p?.name ?? l.product_id} (${l.product_id}) x ${l.quantity}${l.unit_price !== undefined ? ` @ ${l.unit_price} (deviation)` : ""}`;
  });
  parts.push(ls.length ? `Lines: ${ls.join("; ")}.` : "No lines chosen.");
  if (priced.value) {
    const t = priced.value.totals_dkk;
    parts.push(
      `Server totals DKK: monthly ${t.monthly}, annual ${t.annual}, onetime ${t.onetime}, year one ${t.year_one}.`,
    );
  }
  const answered = Object.entries(answers).filter(([, v]) => v);
  if (answered.length) parts.push(`Answers: ${answered.map(([k, v]) => `${k}=${v}`).join("; ")}.`);
  return parts.join("\n");
}

function adoptLines(args: Record<string, unknown>): void {
  const raw = Array.isArray(args.lines) ? (args.lines as Record<string, unknown>[]) : [];
  if (!raw.length) return;
  for (const id of Object.keys(lines)) delete lines[id];
  for (const ln of raw) {
    const id = String(ln.product_id ?? "");
    const qty = Number(ln.quantity ?? 0);
    if (!id || !(qty > 0) || !products.value.some((p) => p.product_id === id)) continue;
    const price = ln.unit_price === undefined || ln.unit_price === null ? null : Number(ln.unit_price);
    lines[id] = { quantity: qty, unitPrice: Number.isFinite(price as number) ? price : null };
  }
  dirty.value = true;
  tab.value = "tilbud";
  filter.value = "valgte";
  void reprice();
}

function adoptCustomer(payload: Record<string, unknown>): void {
  const hits = Array.isArray(payload.customers)
    ? (payload.customers as Record<string, string>[])
    : [];
  const bc = hits.filter((h) => h.source === "Business Central" && h.name);
  const query = String(payload.query ?? "").trim();
  if (bc.length === 1) {
    const c = bc[0];
    customer.name = c.name;
    if (c.cvr) customer.cvr = c.cvr;
    if (c.address) customer.address = c.address;
    if (c.email && !customer.contact_email) customer.contact_email = c.email;
    if (c.phone && !customer.contact_phone) customer.contact_phone = c.phone;
    dirty.value = true;
  } else if (!customer.name && query) {
    customer.name = query;
    dirty.value = true;
  }
}

// Quotes the helper touched this turn: the page shows them when it ends.
let touched = new Set<string>();
const helper = useHelperChat({
  assistantSlug: "service-catalog",
  devUser: () => props.devUser,
  pageContext,
  // A customer's request or asset list is a natural thing to hand over:
  // the helper reads it and prices what it asks for.
  uploads: true,
  onEvent: (evt, turn) => {
    if (evt.type !== "tool_result") return;
    const rt = (turn.tools ?? []).find((t) => t.id === evt.id);
    const target = String(evt.payload?.target_id ?? "");
    if (evt.ok && /^T-\d{4}-\d+$/.test(target)) touched.add(target);
    if (evt.ok && evt.name === "convert_quote_to_contract" && quote.value) {
      touched.add(quote.value.quote_id);
    }
    // The helper priced a draft: put those lines on the page, so what
    // the user reads in the chat is what the form shows.
    if (evt.ok && evt.name === "price_quote" && rt && editable.value) {
      adoptLines(rt.arguments);
    }
    // The helper looked the customer up: one hit fills the block.
    if (evt.ok && evt.name === "find_customer" && editable.value) {
      adoptCustomer(evt.payload);
    }
  },
  afterTurn: async () => {
    // The chat changed a saved quote — show it. A brand-new quote created
    // by the helper is opened as well, so the page and the chat agree.
    const id = [...touched].pop();
    touched = new Set<string>();
    if (id) await loadQuote(id);
  },
});

// ── catalogue editor (owner, co-owners, admins) ──────────────────────────
// The catalogue's defaults are configuration; a price typed on a quote line
// never touches them. This is the one place they change, and only the
// people named on the assistant may use it. What is saved goes through the
// same validation as the config file and lands in the same tables.
type EditRow = EditableProduct & { _key: number; _paramsText: string };
const canEditCatalog = computed(() => !!me.value?.can_edit_catalog);
const catEdit = ref(false);
const catSaving = ref(false);
const catExporting = ref(false);
const draft = ref<EditRow[]>([]);
const draftRate = ref(0);
let keySeq = 0;

function paramsToText(p: Record<string, number>): string {
  return Object.entries(p ?? {})
    .map(([k, v]) => `${k}=${v}`)
    .join(", ");
}
function parseParams(text: string): Record<string, number> {
  const out: Record<string, number> = {};
  for (const part of text.split(/[,;]/)) {
    const [k, v] = part.split("=").map((x) => x.trim());
    if (k) out[k] = Number(String(v ?? "").replace(",", "."));
  }
  return out;
}
/** A product id from its name: lower-case ASCII with dashes. Editable
 * afterwards; only new products get one this way. */
function slugId(name: string): string {
  return name
    .toLowerCase()
    .replace(/æ/g, "ae")
    .replace(/ø/g, "oe")
    .replace(/å/g, "aa")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48);
}
const draftShown = computed(() => {
  const q = search.value.trim().toLowerCase();
  if (!q) return draft.value;
  return draft.value.filter((r) =>
    [r.name, r.category, r.product_id, r.unit].some((x) => (x ?? "").toLowerCase().includes(q)),
  );
});

function startCatalogEdit(): void {
  draft.value = products.value.map((p) => ({
    ...p,
    tiers: p.tiers.map((t) => ({ ...t })),
    formula_params: { ...p.formula_params },
    _key: ++keySeq,
    _paramsText: paramsToText(p.formula_params),
  }));
  draftRate.value = catalog.value?.usd_to_dkk ?? 0;
  catEdit.value = true;
}
function cancelCatalogEdit(): void {
  catEdit.value = false;
  draft.value = [];
}
function addProduct(): void {
  const last = draft.value[draft.value.length - 1];
  draft.value.push({
    product_id: "",
    category: last?.category ?? categories.value[0] ?? "",
    name: "",
    unit: "",
    period: "monthly",
    rule_type: "unit",
    currency: "DKK",
    unit_price: null,
    group_name: null,
    group_subsequent_price: null,
    formula_expr: null,
    formula_params: {},
    tiers: [],
    note: "",
    source_ref: "",
    _key: ++keySeq,
    _paramsText: "",
  });
}
function removeProduct(row: EditRow): void {
  draft.value = draft.value.filter((r) => r !== row);
}
function moveProduct(row: EditRow, dir: -1 | 1): void {
  const i = draft.value.indexOf(row);
  const j = i + dir;
  if (i < 0 || j < 0 || j >= draft.value.length) return;
  const next = [...draft.value];
  [next[i], next[j]] = [next[j]!, next[i]!];
  draft.value = next;
}
function addTier(row: EditRow): void {
  const last = row.tiers[row.tiers.length - 1];
  row.tiers.push({ from_qty: last ? (last.to_qty ?? last.from_qty) + 1 : 1, to_qty: null, unit_price: 0 });
}
function removeTier(row: EditRow, i: number): void {
  row.tiers.splice(i, 1);
}
function idFromName(row: EditRow): void {
  if (!row.product_id.trim() && row.name.trim()) row.product_id = slugId(row.name);
}

async function saveCatalog(): Promise<void> {
  const ok = await confirm({
    title: "Gem katalogets standardpriser?",
    message: `${draft.value.length} produkter. De nye priser gælder alle nye beregninger og tilbud fra nu af; allerede gemte tilbud beholder de priser, de blev gemt med. Filen config/service-catalog.json overskriver ikke længere kataloget.`,
    confirmLabel: "Gem katalog",
  });
  if (!ok) return;
  catSaving.value = true;
  try {
    const rows: EditableProduct[] = draft.value.map((r) => {
      const { _key: _k, _paramsText, ...p } = r;
      void _k;
      return {
        ...p,
        product_id: p.product_id.trim() || slugId(p.name),
        formula_params: p.rule_type === "formula" ? parseParams(_paramsText) : {},
        tiers: p.rule_type === "tier" ? p.tiers : [],
      };
    });
    const res = await api.updateCatalog(rows, draftRate.value, props.devUser);
    const { diff, ...view } = res;
    catalog.value = view;
    catEdit.value = false;
    draft.value = [];
    const parts = [
      diff.changed.length ? `${diff.changed.length} ændret` : "",
      diff.added.length ? `${diff.added.length} ny(e)` : "",
      diff.removed.length ? `${diff.removed.length} fjernet` : "",
    ].filter(Boolean);
    toast.success("Kataloget er gemt", parts.join(" · ") || "Ingen ændringer i produkterne");
    void reprice();
  } catch (err) {
    fail(err, "Kataloget blev ikke gemt");
  } finally {
    catSaving.value = false;
  }
}
async function exportCatalogJson(): Promise<void> {
  catExporting.value = true;
  try {
    const filename = await api.exportCatalog(props.devUser);
    toast.success("Kataloget er eksporteret", filename);
  } catch (err) {
    fail(err, "Kataloget kunne ikke eksporteres");
  } finally {
    catExporting.value = false;
  }
}

const pageTabs = computed<PageTab[]>(() => [
  { key: "tilbud", label: "Tilbud", count: chosenIds.value.length || "" },
  { key: "katalog", label: "Katalog", count: products.value.length },
]);
function onTab(key: string): void {
  if (key === "tilbud" || key === "katalog") tab.value = key;
}
</script>

<template>
  <AssistantPage
    class="sk"
    max-width="1320px"
    :eyebrow="me?.assistant.name ?? ''"
    title="Tilbud"
    subtitle="Vælg services og antal, se totalerne, gem tilbuddet og følg det til kontrakt. Priserne er katalogets; et tal du selv skriver på en linje gælder kun dette tilbud."
    :tabs="loading || loadError ? [] : pageTabs"
    :tab="tab"
    @update:tab="onTab"
  >
      <template #actions>
        <button type="button" class="btn" @click="emit('exit')">← Hub</button>
        <button type="button" class="btn" @click="newQuote">Nyt tilbud</button>
        <button type="button" class="btn" @click="openQuotes">Åbn tilbud</button>
        <button
          v-if="can('save_quote')"
          type="button"
          class="btn btn--primary"
          :disabled="saving || !editable"
          @click="save"
        >
          {{ saving ? "Gemmer…" : quote ? "Gem ændringer" : "Gem tilbud" }}
        </button>
        <button
          type="button"
          class="btn"
          :disabled="exporting || saving || (!quote && !chosenIds.length)"
          :title="quote && !dirty ? `Hent ${quote.quote_id} som Word-dokument` : 'Gemmer tilbuddet og henter det som Word-dokument'"
          @click="exportOffer()"
        >
          {{ exporting ? "Bygger…" : "Hent som Word" }}
        </button>
      </template>
      <template #banner>
        <p v-if="loadError" class="sk__error">{{ loadError }}</p>
        <p v-else-if="loading" class="sk__muted">Henter kataloget…</p>
      </template>

    <template v-if="!loading && !loadError">

      <div class="sk__filterbar">
        <input
          v-model="search"
          type="search"
          class="sk__search"
          placeholder="Søg produkt, kategori eller enhed"
          aria-label="Søg"
        />
        <div class="sk__pills">
          <button
            v-for="f in (['alle', 'valgte', 'udenpris', 'regler'] as const)"
            :key="f"
            type="button"
            class="skpill"
            :aria-pressed="filter === f"
            @click="filter = f"
          >
            {{ { alle: 'Alle', valgte: 'Valgte', udenpris: 'Uden pris', regler: 'Formler og trin' }[f] }}
            <span class="skpill__n">{{ counts[f] }}</span>
          </button>
        </div>
        <span class="sk__spacer" />
        <span v-if="shown.length !== products.length" class="sk__muted"
          >{{ shown.length }} af {{ products.length }} produkter</span
        >
        <button v-if="tab === 'tilbud'" type="button" class="btn btn--small" @click="clearAll">
          Ryd antal
        </button>
      </div>

      <!-- ── Tilbud ────────────────────────────────────────────────── -->
      <div v-if="tab === 'tilbud'" class="sk__cols">
        <section class="sk__main">
          <p v-if="!shown.length" class="sk__empty">Ingen produkter passer på søgningen.</p>
          <details
            v-for="cat in categories"
            v-show="shown.some((p) => p.category === cat)"
            :key="cat"
            class="cat"
            :open="!!search || !closed.has(cat)"
            @toggle="(e: Event) => ((e.target as HTMLDetailsElement).open ? closed.delete(cat) : closed.add(cat))"
          >
            <summary>
              <h2>{{ cat }}</h2>
              <span class="cat__meta">
                {{ shown.filter((p) => p.category === cat).length }} produkter
              </span>
              <span class="cat__sum">
                {{
                  (() => {
                    const s = shown
                      .filter((p) => p.category === cat)
                      .reduce((acc, p) => acc + (pricedLine(p.product_id)?.amount_dkk ?? 0), 0);
                    return s ? money(s) : "";
                  })()
                }}
              </span>
            </summary>
            <div class="rows">
              <div class="row row--head">
                <span>Service</span><span>Antal</span><span>Pris pr. enhed</span><span>Valuta</span
                ><span>Linjepris</span>
              </div>
              <div
                v-for="p in shown.filter((x) => x.category === cat)"
                :key="p.product_id"
                class="row"
                :class="{ 'row--active': qtyOf(p.product_id) > 0 }"
              >
                <div class="name">
                  <div class="name__line">
                    <strong>{{ p.name }}</strong>
                    <span class="chip chip--per">{{ PERIOD_SHORT[p.period] }}</span>
                    <span class="chip" :class="{ 'chip--plain': p.rule_type === 'unit' }">{{
                      RULE_LABEL[p.rule_type]
                    }}</span>
                  </div>
                  <small>
                    {{
                      [
                        p.unit ? `pr. ${p.unit}` : "",
                        p.note,
                        lines[p.product_id]?.unitPrice !== null && lines[p.product_id]?.unitPrice !== undefined
                          ? `afviger fra listepris ${num(listUnitPrice(p))}`
                          : "",
                      ]
                        .filter(Boolean)
                        .join(" · ")
                    }}
                  </small>
                </div>
                <input
                  type="number"
                  min="0"
                  step="1"
                  :value="qtyOf(p.product_id) || ''"
                  placeholder="0"
                  :disabled="!editable"
                  :aria-label="`Antal ${p.name}`"
                  @input="setQty(p.product_id, ($event.target as HTMLInputElement).value)"
                />
                <input
                  type="number"
                  step="0.01"
                  class="price"
                  :class="{
                    'price--derived': p.rule_type !== 'unit' && lines[p.product_id]?.unitPrice == null,
                    'price--override': lines[p.product_id]?.unitPrice != null,
                  }"
                  :value="
                    lines[p.product_id]?.unitPrice != null
                      ? lines[p.product_id].unitPrice
                      : listUnitPrice(p) ?? ''
                  "
                  placeholder="—"
                  :disabled="!editable"
                  :title="
                    lines[p.product_id]?.unitPrice != null
                      ? 'Pris for dette tilbud. Tøm feltet for at gå tilbage til listeprisen.'
                      : 'Listepris fra kataloget. Skriv et tal for at afvige på dette tilbud.'
                  "
                  :aria-label="`Pris pr. ${p.unit || 'enhed'} for ${p.name}`"
                  @change="setPrice(p.product_id, ($event.target as HTMLInputElement).value)"
                />
                <span class="cur">{{ p.currency ?? (lines[p.product_id]?.unitPrice != null ? "DKK" : "—") }}</span>
                <span class="lineval" :class="{ 'lineval--zero': !pricedLine(p.product_id)?.amount }">
                  {{
                    (() => {
                      const ln = pricedLine(p.product_id);
                      if (!ln) return "—";
                      if (ln.status === "ok" && ln.amount) return money(ln.amount, ln.currency ?? "DKK");
                      if (ln.status === "no-price") return "Pris mangler";
                      if (ln.status === "no-currency") return "Valuta mangler";
                      if (ln.status === "no-tier") return "Intet trin";
                      if (ln.status === "bad-formula") return "Formelfejl";
                      return "—";
                    })()
                  }}
                </span>
              </div>
            </div>
            <div v-if="questionsFor(cat).length && !search && filter === 'alle'" class="qbox">
              <h4>Servicespørgsmål</h4>
              <div class="qgrid">
                <label v-for="q in questionsFor(cat)" :key="q.question_id" class="field">
                  <span>{{ q.label }}</span>
                  <select v-if="q.kind === 'choice'" v-model="answers[q.label]" :disabled="!editable">
                    <option value="">Vælg…</option>
                    <option v-for="o in q.options" :key="o" :value="o">{{ o }}</option>
                  </select>
                  <input
                    v-else
                    v-model="answers[q.label]"
                    :type="q.kind === 'number' ? 'number' : 'text'"
                    :disabled="!editable"
                  />
                </label>
              </div>
            </div>
          </details>
        </section>

        <aside class="sk__side">
          <section class="panel">
            <h3>Kunde</h3>
            <div class="panel__body">
              <label class="field">
                <span>Navn</span>
                <div class="lookup">
                  <input
                    v-model="customer.name"
                    type="text"
                    placeholder="Kundens juridiske navn"
                    autocomplete="off"
                    :disabled="!editable"
                    @input="onCustomerInput"
                    @blur="hideHits"
                  />
                  <ul v-if="customerHits.length" class="lookup__list" role="listbox">
                    <li
                      v-for="c in customerHits"
                      :key="c.source + c.name + c.cvr"
                      role="option"
                      @mousedown.prevent="pickCustomer(c)"
                    >
                      <span>{{ c.name }}</span>
                      <small>{{ [c.cvr, c.address, c.source].filter(Boolean).join(" · ") }}</small>
                    </li>
                  </ul>
                </div>
              </label>
              <div class="grid2">
                <label class="field"><span>CVR</span><input v-model="customer.cvr" :disabled="!editable" @input="dirty = true" /></label>
                <label class="field"><span>Kontaktperson</span><input v-model="customer.contact_name" :disabled="!editable" @input="dirty = true" /></label>
                <label class="field"><span>E-mail</span><input v-model="customer.contact_email" type="email" :disabled="!editable" @input="dirty = true" /></label>
                <label class="field"><span>Telefon</span><input v-model="customer.contact_phone" :disabled="!editable" @input="dirty = true" /></label>
              </div>
              <label class="field"><span>Adresse</span><input v-model="customer.address" :disabled="!editable" @input="dirty = true" /></label>
              <label class="field"><span>Titel på tilbuddet</span><input v-model="title" :placeholder="customer.name ? `Tilbud til ${customer.name}` : 'Tilbud'" :disabled="!editable" @input="dirty = true" /></label>
              <label class="field"><span>Noter</span><textarea v-model="notes" rows="2" :disabled="!editable" @input="dirty = true" /></label>
            </div>
          </section>

          <section class="panel">
            <h3>Tilbudsoversigt</h3>
            <div class="panel__body">
              <div class="tot"><span class="tot__k">Månedligt</span><span class="tot__v">{{ money(priced?.totals_dkk.monthly ?? 0) }}</span></div>
              <div v-if="priced?.by_period_and_currency?.monthly?.USD" class="split">
                heraf {{ money(priced.by_period_and_currency.monthly.USD, "USD") }} omregnet med kurs {{ num(priced.usd_to_dkk) }}
              </div>
              <div class="tot"><span class="tot__k">Årlige poster</span><span class="tot__v">{{ money(priced?.totals_dkk.annual ?? 0) }}</span></div>
              <div class="tot"><span class="tot__k">Engangsbeløb</span><span class="tot__v">{{ money(priced?.totals_dkk.onetime ?? 0) }}</span></div>
              <div class="tot tot--big"><span class="tot__k">År 1 i alt</span><span class="tot__v">{{ money(priced?.totals_dkk.year_one ?? 0) }}</span></div>
              <div class="split">12 × månedligt + årlige poster + engangs{{ pricing ? " · regner…" : "" }}</div>
            </div>
          </section>

          <section class="panel">
            <h3>Status</h3>
            <div class="panel__body">
              <template v-if="quote">
                <div class="tot"><span class="tot__k">Tilbud</span><span class="tot__v mono">{{ quote.quote_id }}</span></div>
                <div class="tot"><span class="tot__k">Status</span><span class="tot__v"><span class="chip" :class="`chip--${quote.status}`">{{ STATUS_LABEL[quote.status] }}</span><span v-if="dirty" class="sk__muted"> · ugemte ændringer</span></span></div>
                <div class="tot"><span class="tot__k">Godkendelser</span>
                  <span class="tot__v">
                    <span v-for="p in (['customer', 'seller', 'ops'] as Party[])" :key="p" class="appr" :class="{ 'appr--ok': !!quote.approvals[p] }" :title="quote.approvals[p] ? `${quote.approvals[p]?.by} · ${quote.approvals[p]?.at}` : 'mangler'">
                      {{ PARTY_LABEL[p] }} {{ quote.approvals[p] ? "✓" : "–" }}
                    </span>
                  </span>
                </div>
                <div v-if="quote.contract_id" class="tot"><span class="tot__k">Kontrakt</span><span class="tot__v mono">{{ quote.contract_id }}</span></div>
                <div class="btnrow">
                  <button
                    v-if="quote.contract_id"
                    type="button"
                    class="btn btn--small"
                    :disabled="exporting"
                    title="Contracts' serviceaftale-skabelon, udfyldt fra kontrakten"
                    @click="exportContract"
                  >
                    Hent serviceaftale
                  </button>
                  <button
                    v-else
                    type="button"
                    class="btn btn--small"
                    :disabled="exporting"
                    title="Contracts' serviceaftale-skabelon, udfyldt fra tilbuddet — løbetid og opsigelse udfyldes, når det er en kontrakt"
                    @click="exportOffer('agreement')"
                  >
                    Hent serviceaftale (udkast)
                  </button>
                </div>
                <div class="btnrow">
                  <button v-if="quote.status === 'draft' && can('set_quote_status')" type="button" class="btn btn--small" @click="setStatus('sent')">Markér sendt</button>
                  <button v-if="quote.status === 'sent' && can('set_quote_status')" type="button" class="btn btn--small" @click="setStatus('draft')">Tilbage til kladde</button>
                  <button v-if="['draft', 'sent', 'approved'].includes(quote.status) && can('set_quote_status')" type="button" class="btn btn--small btn--danger" @click="setStatus('cancelled')">Annullér</button>
                  <button v-if="quote.can_delete" type="button" class="btn btn--small btn--danger" @click="deleteQuote(quote)">Slet</button>
                </div>
                <div v-if="['draft', 'sent'].includes(quote.status) && can('record_quote_approval')" class="approve">
                  <input v-model="approvalName" type="text" placeholder="Godkendt af (navn)" aria-label="Godkendt af" />
                  <div class="btnrow">
                    <button v-for="p in (['customer', 'seller', 'ops'] as Party[])" :key="p" type="button" class="btn btn--small" :disabled="!!quote.approvals[p]" @click="approve(p)">
                      {{ PARTY_LABEL[p] }} godkender
                    </button>
                  </div>
                </div>
                <div v-if="quote.status === 'approved' && can('convert_quote_to_contract')" class="convert">
                  <button v-if="!convertOpen" type="button" class="btn btn--primary btn--small" @click="convertOpen = true">Lav kontrakt</button>
                  <template v-else>
                    <div class="grid2">
                      <label class="field"><span>Startdato</span><input v-model="convertForm.start_date" type="date" /></label>
                      <label class="field"><span>Binding (mdr.)</span><input v-model.number="convertForm.term_months" type="number" min="0" /></label>
                      <label class="field"><span>Opsigelse (mdr.)</span><input v-model.number="convertForm.notice_months" type="number" min="0" /></label>
                    </div>
                    <label class="field"><span>Noter</span><input v-model="convertForm.notes" /></label>
                    <div class="btnrow">
                      <button type="button" class="btn btn--small" @click="convertOpen = false">Fortryd</button>
                      <button type="button" class="btn btn--primary btn--small" @click="convert">Opret kontrakt</button>
                    </div>
                  </template>
                </div>
              </template>
              <p v-else class="sk__muted">Ikke gemt endnu. Gem tilbuddet for at få et nummer og kunne følge det.</p>
            </div>
          </section>

          <section class="panel">
            <h3>Skal afklares</h3>
            <div class="panel__body">
              <ul class="flags">
                <li v-for="f in priced?.flags ?? []" :key="f">{{ f }}</li>
                <li v-for="q in openQuestions" :key="q.label">{{ q.category }}: {{ q.label }}</li>
                <li v-if="unpricedChosen.length === 0 && !(priced?.flags ?? []).length && !openQuestions.length" class="flags__ok">
                  Alle valgte linjer har en pris, og spørgsmålene er besvaret.
                </li>
              </ul>
            </div>
          </section>
        </aside>
      </div>

      <!-- ── Katalog ───────────────────────────────────────────────── -->
      <div v-else class="tablecard">
        <div class="sk__note sk__note--row">
          <template v-if="!catEdit">
            <span v-if="canEditCatalog">
              Standardpriserne her gælder alle nye tilbud.
              <template v-if="catalog?.source?.source === 'editor'">
                Sidst gemt af {{ catalog.source.edited_by }} {{ (catalog.source.edited_at ?? "").slice(0, 10) }}.
              </template>
              <template v-else>Indlæst fra <code>config/service-catalog.json</code>.</template>
            </span>
            <span v-else>
              Katalogets priser og regler vedligeholdes af ejeren. Et tal du skriver på et tilbud afviger kun der.
            </span>
            <span class="sk__spacer" />
            <template v-if="canEditCatalog">
              <button type="button" class="btn btn--small" :disabled="catExporting" @click="exportCatalogJson">
                Eksportér JSON
              </button>
              <button type="button" class="btn btn--small btn--primary" @click="startCatalogEdit">Redigér katalog</button>
            </template>
          </template>
          <template v-else>
            <span>Redigerer standardpriser · {{ draft.length }} produkter</span>
            <label class="rate">USD→DKK <input v-model.number="draftRate" type="number" step="0.01" min="0" aria-label="USD til DKK" /></label>
            <span class="sk__spacer" />
            <button type="button" class="btn btn--small" :disabled="catSaving" @click="cancelCatalogEdit">Fortryd</button>
            <button type="button" class="btn btn--small btn--primary" :disabled="catSaving" @click="saveCatalog">
              {{ catSaving ? "Gemmer…" : "Gem katalog" }}
            </button>
          </template>
        </div>
        <table v-if="catEdit" class="catalog catalog--edit">
          <thead>
            <tr><th>Produkt</th><th>Kategori</th><th>Enhed</th><th>Periode</th><th>Regel</th><th class="num">Pris pr. enhed</th><th>Valuta</th><th></th></tr>
          </thead>
          <tbody>
            <tr v-for="row in draftShown" :key="row._key">
              <td class="edit__name">
                <input v-model="row.name" placeholder="Navn" :aria-label="`Navn`" @blur="idFromName(row)" />
                <div class="edit__sub">
                  <input v-model="row.product_id" class="mono" placeholder="id" aria-label="Produkt-id" />
                  <input v-model="row.note" placeholder="Note" aria-label="Note" />
                </div>
              </td>
              <td><input v-model="row.category" list="sk-cats" placeholder="Kategori" aria-label="Kategori" /></td>
              <td><input v-model="row.unit" placeholder="fx server" aria-label="Enhed" /></td>
              <td>
                <select v-model="row.period" aria-label="Periode">
                  <option v-for="(l, k) in PERIOD_LONG" :key="k" :value="k">{{ l }}</option>
                </select>
              </td>
              <td class="edit__rule">
                <select v-model="row.rule_type" aria-label="Regel">
                  <option v-for="(l, k) in RULE_LABEL" :key="k" :value="k">{{ l }}</option>
                </select>
                <template v-if="row.rule_type === 'group'">
                  <input v-model="row.group_name" placeholder="Gruppe (fx hypervisor)" aria-label="Gruppe" />
                  <input v-model.number="row.group_subsequent_price" type="number" step="0.01" placeholder="Pris for de næste" aria-label="Efterfølgende pris" />
                </template>
                <template v-else-if="row.rule_type === 'formula'">
                  <input v-model="row.formula_expr" class="mono" placeholder="fx loft(antal / 10) * pris" aria-label="Formel" />
                  <input v-model="row._paramsText" class="mono" placeholder="pris=615, faktor=1,2" aria-label="Parametre" />
                </template>
                <template v-else-if="row.rule_type === 'tier'">
                  <div v-for="(t, ti) in row.tiers" :key="ti" class="edit__tier">
                    <input v-model.number="t.from_qty" type="number" placeholder="fra" aria-label="Fra antal" />
                    <input v-model.number="t.to_qty" type="number" placeholder="til (tom = ∞)" aria-label="Til antal" />
                    <input v-model.number="t.unit_price" type="number" step="0.01" placeholder="pris" aria-label="Trinpris" />
                    <button type="button" class="btn btn--small" aria-label="Fjern trin" @click="removeTier(row, ti)">×</button>
                  </div>
                  <button type="button" class="btn btn--small" @click="addTier(row)">+ trin</button>
                </template>
              </td>
              <td class="num">
                <input
                  v-if="row.rule_type === 'unit' || row.rule_type === 'group'"
                  v-model.number="row.unit_price"
                  type="number"
                  step="0.01"
                  placeholder="—"
                  :aria-label="`Pris pr. enhed for ${row.name || 'nyt produkt'}`"
                />
                <span v-else>—</span>
              </td>
              <td>
                <select v-model="row.currency" aria-label="Valuta">
                  <option :value="null">—</option>
                  <option value="DKK">DKK</option>
                  <option value="USD">USD</option>
                </select>
              </td>
              <td class="edit__actions">
                <button type="button" class="btn btn--small" aria-label="Flyt op" @click="moveProduct(row, -1)">↑</button>
                <button type="button" class="btn btn--small" aria-label="Flyt ned" @click="moveProduct(row, 1)">↓</button>
                <button type="button" class="btn btn--small btn--danger" @click="removeProduct(row)">Fjern</button>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-if="catEdit" class="edit__foot">
          <button type="button" class="btn btn--small" @click="addProduct">+ Tilføj service</button>
          <span class="sk__muted">Rækkefølgen her er rækkefølgen i kataloget. Servicespørgsmål redigeres ikke her endnu.</span>
        </div>
        <datalist id="sk-cats"><option v-for="c in categories" :key="c" :value="c" /></datalist>
        <table v-if="!catEdit" class="catalog">
          <thead>
            <tr><th>Produkt</th><th>Enhed</th><th>Periode</th><th>Regel</th><th class="num">Pris pr. enhed</th><th>Valuta</th><th>Kilde</th></tr>
          </thead>
          <tbody>
            <template v-for="cat in categories" :key="cat">
              <tr v-if="shown.some((p) => p.category === cat)" class="catgroup"><td colspan="7">{{ cat }}</td></tr>
              <tr v-for="p in shown.filter((x) => x.category === cat)" :key="p.product_id">
                <td><div class="pname">{{ p.name }}</div><div v-if="p.note" class="pnote">{{ p.note }}</div></td>
                <td>{{ p.unit }}</td>
                <td>{{ PERIOD_LONG[p.period] }}</td>
                <td class="pnote">
                  {{ RULE_LABEL[p.rule_type] }}
                  <template v-if="p.rule_type === 'group'"> · første {{ num(p.unit_price) }}, derefter {{ num(p.group_subsequent_price) }}</template>
                  <template v-else-if="p.rule_type === 'formula'"> · <code>{{ p.formula_expr }}</code></template>
                  <template v-else-if="p.rule_type === 'tier'"> · {{ p.tiers.length }} trin</template>
                </td>
                <td class="num">{{ p.unit_price === null ? "—" : num(p.unit_price) }}</td>
                <td>{{ p.currency ?? "—" }}</td>
                <td class="pnote">{{ p.source_ref }}</td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </template>

    <!-- ── Åbn tilbud ────────────────────────────────────────────── -->
    <div v-if="quotesOpen" class="drawer" role="dialog" aria-label="Gemte tilbud">
      <div class="drawer__head">
        <h3>Gemte tilbud</h3>
        <input v-model="quoteSearch" type="search" placeholder="Kunde, CVR eller nummer" aria-label="Søg tilbud" />
        <button type="button" class="btn btn--small" @click="quotesOpen = false">Luk</button>
      </div>
      <p v-if="quotesLoading" class="sk__muted">Henter…</p>
      <p v-else-if="!quoteList.length" class="sk__muted">Ingen gemte tilbud endnu.</p>
      <ul v-else class="drawer__list">
        <li v-for="q in quoteList" :key="q.quote_id" class="drawer__item">
          <button
            v-if="q.can_delete"
            type="button"
            class="drawer__del"
            :aria-label="`Slet ${q.quote_id}`"
            title="Slet tilbuddet"
            @click="deleteQuote(q)"
          >
            Slet
          </button>
          <button type="button" class="drawer__open" @click="loadQuote(q.quote_id)">
            <span class="mono">{{ q.quote_id }}</span>
            <strong>{{ q.customer_name }}</strong>
            <span class="chip" :class="`chip--${q.status}`">{{ STATUS_LABEL[q.status] }}</span>
            <span class="mono">{{ money(q.year_one_dkk) }}</span>
            <small>{{ q.updated_at?.slice(0, 10) }} · mangler: {{ q.missing_approvals.map((p) => PARTY_LABEL[p]).join(", ") || "ingen" }}</small>
          </button>
        </li>
      </ul>
    </div>

    <!-- ── Hjælper ───────────────────────────────────────────────── -->
    <HelperPanel
      :helper="helper"
      title="Servicekatalog-hjælper"
      hint="ser det tilbud, du har på skærmen"
      empty='Fx: "Hvad ligger bag onboarding-prisen?", "Gem det her til Kunde A/S", "Find vores sidste tilbud til Beumer". Eller vedhæft kundens forespørgsel med 📎 og bed om et tilbud på det, den beder om.'
      :dev-user="devUser"
    />
  </AssistantPage>
</template>

<style scoped>
.sk__error { color: var(--color-error); }
.sk__muted { color: var(--color-muted); font-size: 12.5px; }
.sk__note { color: var(--color-muted); font-size: 13px; padding: 12px 16px; margin: 0; border-bottom: 1px solid var(--color-border); }
.sk__note--row { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; }
.rate input { width: 80px; margin-left: 6px; }
.catalog--edit input, .catalog--edit select { width: 100%; min-width: 80px; font: inherit; font-size: 13px; padding: 4px 6px; border: 1px solid var(--color-border); border-radius: 6px; background: var(--color-surface); color: inherit; box-sizing: border-box; }
.catalog--edit td { vertical-align: top; }
.catalog--edit .edit__name { min-width: 220px; }
.edit__sub { display: flex; gap: 6px; margin-top: 4px; }
.edit__rule { display: grid; gap: 4px; min-width: 230px; }
.edit__tier { display: grid; grid-template-columns: 1fr 1fr 1fr auto; gap: 4px; }
.edit__actions { white-space: nowrap; }
.edit__actions .btn { margin-right: 4px; }
.edit__foot { display: flex; gap: 12px; align-items: center; padding: 10px 14px; }
.sk__empty { color: var(--color-muted); padding: 40px; text-align: center; background: var(--color-surface); border: 1px dashed var(--color-border); border-radius: var(--radius-lg); min-height: 200px; display: grid; place-items: center; }

.btn { background: var(--color-surface); border: 1px solid var(--color-border); border-radius: var(--radius-md); padding: 7px 12px; font-size: 13px; cursor: pointer; color: var(--color-ink); white-space: nowrap; }
.btn:hover { border-color: var(--color-accent); }
.btn:disabled { opacity: 0.55; cursor: default; }
.btn--primary { background: var(--color-accent); border-color: var(--color-accent); color: var(--color-inverse); }
.btn--primary:hover { background: var(--color-accent-hover); }
.btn--danger { color: var(--color-error); }
.btn--small { padding: 4px 9px; font-size: 12px; }
.btnrow { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }


.sk__filterbar { position: sticky; top: 0; z-index: 5; background: var(--color-surface); border: 1px solid var(--color-border); border-radius: var(--radius-lg); padding: 12px 16px; margin-bottom: 18px; min-height: 60px; display: flex; flex-wrap: wrap; gap: 10px; align-items: center; box-shadow: var(--shadow-sm); }
.sk__search { flex: 1 1 260px; max-width: 460px; padding: 6px 10px; border: 1px solid var(--color-border); border-radius: var(--radius-md); background: var(--color-surface); color: inherit; }
.sk__pills { display: flex; gap: 4px; flex-wrap: wrap; }
.skpill { background: var(--color-surface); border: 1px solid var(--color-border); border-radius: var(--radius-pill); padding: 5px 12px; font-size: 12.5px; cursor: pointer; color: var(--color-muted); font: inherit; font-size: 12.5px; }
.skpill[aria-pressed="true"] { border-color: var(--color-accent); color: var(--color-accent); font-weight: 600; }
.skpill__n { font-family: var(--font-mono); font-size: 11px; opacity: 0.75; margin-left: 4px; }
.sk__spacer { flex: 1; }

.sk__cols { display: grid; grid-template-columns: minmax(0, 1fr) 344px; gap: 26px; align-items: start; width: 100%; }
.sk__main { min-width: 0; }
@media (max-width: 1100px) { .sk__cols { grid-template-columns: minmax(0, 1fr); } }

.cat { background: var(--color-surface); border: 1px solid var(--color-border); border-radius: var(--radius-lg); margin-bottom: 16px; box-shadow: var(--shadow-sm); overflow: hidden; }
.cat > summary { list-style: none; cursor: pointer; padding: 13px 18px; display: flex; align-items: baseline; gap: 12px; border-bottom: 1px solid var(--color-border); }
.cat > summary::-webkit-details-marker { display: none; }
.cat h2 { font-family: var(--font-display); font-size: 16.5px; margin: 0; }
.cat__meta { margin-left: auto; font-size: 12px; color: var(--color-muted); font-family: var(--font-mono); }
.cat__sum { font-family: var(--font-mono); font-size: 13px; color: var(--color-muted); }
.rows { padding: 2px 0 6px; }
.row { display: grid; grid-template-columns: minmax(170px, 1fr) 84px 112px 56px 132px; gap: 10px; align-items: center; padding: 8px 18px; border-bottom: 1px solid var(--color-border); }
.row:last-child { border-bottom: none; }
.row--head { font-size: 10.5px; letter-spacing: 0.1em; text-transform: uppercase; color: var(--color-muted); font-weight: 600; }
.row--head span:nth-child(n + 2), .cur, .lineval { text-align: right; }
.row--active { background: color-mix(in srgb, var(--color-accent) 8%, transparent); }
.name { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.name__line { display: flex; align-items: center; gap: 7px; flex-wrap: wrap; }
.name strong { font-weight: 550; font-size: 14px; }
.name small { color: var(--color-muted); font-size: 11.5px; }
.row input[type="number"] { width: 100%; text-align: right; font-family: var(--font-mono); padding: 6px 8px; border: 1px solid var(--color-border); border-radius: var(--radius-sm); background: var(--color-surface-alt); color: inherit; font-size: 13px; }
.price--derived { border-style: dashed; border-color: var(--color-accent); }
.price--override { border-color: var(--color-warn); background: var(--color-warn-bg); }
.cur { font-size: 12.5px; color: var(--color-muted); }
.lineval { font-family: var(--font-mono); font-size: 13.5px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.lineval--zero { color: var(--color-muted); }
.chip { display: inline-block; font-size: 10.5px; letter-spacing: 0.06em; text-transform: uppercase; padding: 2px 6px; border-radius: 3px; font-weight: 600; white-space: nowrap; background: color-mix(in srgb, var(--color-accent) 14%, transparent); color: var(--color-accent); }
.chip--per, .chip--plain { background: var(--color-surface-alt); color: var(--color-muted); }
.chip--approved { background: var(--color-success-bg); color: var(--color-success); }
.chip--contract { background: var(--color-success-bg); color: var(--color-success); }
.chip--cancelled { background: var(--color-error-bg); color: var(--color-error); }
.chip--sent { background: var(--color-warn-bg); color: var(--color-warn-ink); }
.qbox { padding: 10px 18px 14px; background: var(--color-surface-alt); border-top: 1px solid var(--color-border); }
.qbox h4 { font-size: 11px; letter-spacing: 0.1em; text-transform: uppercase; color: var(--color-muted); margin: 0 0 8px; }
.qgrid { display: grid; grid-template-columns: repeat(auto-fit, minmax(210px, 1fr)); gap: 10px; }
.field { display: flex; flex-direction: column; gap: 4px; }
.field > span { font-size: 12px; color: var(--color-muted); }
.field input, .field select, .field textarea { width: 100%; padding: 6px 8px; border: 1px solid var(--color-border); border-radius: var(--radius-sm); background: var(--color-surface-alt); color: inherit; font: inherit; font-size: 13.5px; }
.grid2 { display: grid; grid-template-columns: repeat(auto-fit, minmax(130px, 1fr)); gap: 10px; }

.sk__side { position: sticky; top: 64px; display: flex; flex-direction: column; gap: 14px; }
@media (max-width: 1100px) { .sk__side { position: static; } }
.panel { background: var(--color-surface); border: 1px solid var(--color-border); border-radius: var(--radius-lg); box-shadow: var(--shadow-sm); }
.panel > h3 { font-size: 11px; letter-spacing: 0.12em; text-transform: uppercase; color: var(--color-muted); font-weight: 600; padding: 13px 16px 0; margin: 0; }
.panel__body { padding: 10px 16px 16px; display: flex; flex-direction: column; gap: 8px; }
.tot { display: flex; justify-content: space-between; align-items: baseline; gap: 10px; padding: 5px 0; border-bottom: 1px dashed var(--color-border); }
.tot__k { font-size: 13px; color: var(--color-muted); }
.tot__v { font-family: var(--font-mono); font-size: 14px; }
.tot--big .tot__k { font-size: 14px; color: var(--color-ink); font-weight: 600; }
.tot--big .tot__v { font-size: 21px; font-family: var(--font-display); }
.split { font-family: var(--font-mono); font-size: 11.5px; color: var(--color-muted); }
.mono { font-family: var(--font-mono); }
.appr { display: inline-block; margin-left: 8px; font-size: 12px; color: var(--color-muted); }
.appr--ok { color: var(--color-success); font-weight: 600; }
.approve, .convert { display: flex; flex-direction: column; gap: 6px; margin-top: 6px; }
.approve input { padding: 6px 8px; border: 1px solid var(--color-border); border-radius: var(--radius-sm); background: var(--color-surface-alt); color: inherit; }
.flags { margin: 0; padding: 0; list-style: none; display: flex; flex-direction: column; gap: 7px; }
.flags li { font-size: 12.5px; color: var(--color-muted); padding-left: 10px; border-left: 3px solid var(--color-warn); }
.flags__ok { border-left-color: var(--color-success) !important; }
.lookup { position: relative; }
.lookup input { width: 100%; padding: 6px 8px; border: 1px solid var(--color-border); border-radius: var(--radius-sm); background: var(--color-surface-alt); color: inherit; font-size: 13.5px; }
.lookup__list { position: absolute; left: 0; right: 0; top: calc(100% + 4px); z-index: 40; margin: 0; padding: 4px; list-style: none; background: var(--color-surface); border: 1px solid var(--color-border); border-radius: var(--radius-md); box-shadow: var(--shadow-lg); max-height: 224px; overflow-y: auto; }
.lookup__list li { padding: 6px 8px; border-radius: 4px; cursor: pointer; display: flex; flex-direction: column; font-size: 13px; }
.lookup__list li:hover { background: var(--color-surface-alt); }
.lookup__list small { color: var(--color-muted); font-size: 11.5px; }

.tablecard { background: var(--color-surface); border: 1px solid var(--color-border); border-radius: var(--radius-lg); overflow: auto; }
.catalog { border-collapse: collapse; width: 100%; font-size: 13.5px; }
.catalog th { text-align: left; padding: 10px 14px; font-size: 10.5px; letter-spacing: 0.09em; text-transform: uppercase; color: var(--color-muted); border-bottom: 1px solid var(--color-border); }
.catalog td { padding: 9px 14px; border-bottom: 1px solid var(--color-border); vertical-align: middle; }
.catalog .num { text-align: right; font-family: var(--font-mono); }
.catgroup td { background: var(--color-surface-alt); font-family: var(--font-display); font-weight: 600; }
.pname { font-weight: 550; }
.pnote { color: var(--color-muted); font-size: 11.5px; }

.drawer { position: fixed; top: 0; right: 0; bottom: 0; width: min(520px, 96vw); z-index: 50; background: var(--color-surface); border-left: 1px solid var(--color-border); box-shadow: var(--shadow-lg); padding: 16px; overflow-y: auto; }
.drawer__head { display: flex; gap: 8px; align-items: center; margin-bottom: 12px; }
.drawer__head h3 { margin: 0; flex: 1; font-family: var(--font-display); }
.drawer__head input { flex: 2; padding: 6px 8px; border: 1px solid var(--color-border); border-radius: var(--radius-sm); background: var(--color-surface-alt); color: inherit; }
.drawer__list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 6px; }
.drawer__item { position: relative; }
.drawer__del { position: absolute; top: 6px; right: 8px; z-index: 1; padding: 2px 8px; font: inherit; font-size: 11.5px; color: var(--color-error); background: var(--color-surface); border: 1px solid var(--color-border); border-radius: var(--radius-sm); cursor: pointer; }
.drawer__del:hover { border-color: var(--color-error); }
.drawer__list .drawer__open { width: 100%; text-align: left; display: grid; grid-template-columns: auto 1fr auto auto; gap: 8px; align-items: baseline; padding: 8px 10px; background: var(--color-surface-alt); border: 1px solid var(--color-border); border-radius: var(--radius-md); cursor: pointer; color: inherit; font: inherit; }
.drawer__list .drawer__open:hover { border-color: var(--color-accent); }
.drawer__item:has(.drawer__del) .drawer__open { padding-right: 56px; }
.drawer__list small { grid-column: 1 / -1; color: var(--color-muted); font-size: 11.5px; }

</style>
